import { FilterFilled } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Card, DatePicker, Descriptions, Select, Space } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import {
  ACTIONS,
  LOGS_TABLE_FIELDS,
  MODULE_NAMES,
} from '../../common/constants';
import CommonTable from '../../components/CommonTable';
import {
  antdSortOrder,
  mapAntTableChangeToParams,
  useTableQueryParams,
} from '../../hooks/useTableQueryParams';
import LogDescription from './components/LogDescription';
import { LOG_ENTRIES } from './graphql/Queries';
import './logs.less';

const { RangePicker } = DatePicker;

const buildLogsFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  startDate: params?.dateRange?.from || '',
  endDate: params?.dateRange?.to || '',
  sortOrder: params?.sortOrder,
  moduleName: params?.moduleName,
  actionType: params?.actionType,
});

function Logs() {
  const { params, setParams } = useTableQueryParams({
    fields: LOGS_TABLE_FIELDS,
  });

  const [executeQuery, { data }] = useLazyQuery(LOG_ENTRIES, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  useEffect(() => {
    executeQuery({ variables: { filters: buildLogsFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const onChange = (pagination, _filters, sorter) => {
    setParams(mapAntTableChangeToParams(pagination, sorter, params?.pageSize));
  };

  const onRangeChange = (dates) => {
    setParams({
      dateRange: dates
        ? {
            from: dayjs(dates?.[0])?.format('YYYY-MM-DD'),
            to: dayjs(dates?.[1])?.format('YYYY-MM-DD'),
          }
        : { from: null, to: null },
    });
  };

  const handleModuleNameFilter = (value) => {
    setParams({ moduleName: value, page: 1 });
  };
  const handleActionFilter = (value) => {
    setParams({ actionType: value, page: 1 });
  };

  const getModuleFilterProps = () => ({
    filterSearch: true,
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <Select
            showSearch
            allowClear
            placeholder="Select Module"
            onChange={handleModuleNameFilter}
            filterOption={(inputValue, option) =>
              option?.value?.toLowerCase()?.includes(inputValue?.toLowerCase())
            }
            options={MODULE_NAMES}
            virtual={false}
          />
        </Space>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled
        className={params?.moduleName ? 'filter-filled-data' : ''}
      />
    ),
  });

  const getActionFilterProps = () => ({
    filterSearch: true,
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <Select
            showSearch
            allowClear
            placeholder="Select Action"
            onChange={handleActionFilter}
            filterOption={(inputValue, option) =>
              option?.value?.toLowerCase()?.includes(inputValue?.toLowerCase())
            }
            options={ACTIONS}
            virtual={false}
          />
        </Space>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled
        className={params?.actionType ? 'filter-filled-data' : ''}
      />
    ),
  });

  const columns = [
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      sortOrder: antdSortOrder(params, 'updatedAt'),
      render: (value) => dayjs(value)?.format('hh:mm:ss DD/MM/YYYY'),
    },
    {
      title: 'Action By',
      dataIndex: 'actionBy',
      key: 'actionBy',
      render: (value) => value?.displayName || value?.firstName,
    },
    {
      title: 'Module',
      dataIndex: 'moduleName',
      key: 'moduleName',
      ...getModuleFilterProps(),
    },
    {
      title: 'Action',
      dataIndex: 'actionType',
      key: 'actionType',
      ...getActionFilterProps(),
    },
  ];

  return (
    <>
      <div className="table-card-page">
        <Card
          title="Logs"
          className="ant-body-scroll"
          extra={
            <div className="project-filter-right">
              <div className="project-filter">
                <RangePicker onChange={onRangeChange} />
              </div>
            </div>
          }
        >
          <CommonTable
            size="small"
            columns={columns}
            dataSource={data?.eventLogsEntries?.logEntries}
            rowKey={(record) => record?.id}
            className="expandable-row-table logs-table"
            bordered
            onChange={onChange}
            paginationConfig={{
              current: params?.page,
              total: data?.eventLogsEntries?.count,
              pageSize: params?.pageSize,
            }}
            expandable={{
              expandedRowRender: (record) => (
                <div className="expandable-description">
                  <Descriptions bordered>
                    <Descriptions.Item span={8} label="Description:">
                      {record?.description || '-'}
                    </Descriptions.Item>
                    {Object?.keys(record?.newData)?.length && (
                      <Descriptions.Item span={2} label="New Data:">
                        <div>
                          {Object?.keys(record?.newData)?.map((item, index) => (
                            <div className="d-flex" key={`record-${index + 1}`}>
                              {item} :
                              <LogDescription
                                data={record?.newData?.[item]}
                                item={item}
                              />
                            </div>
                          ))}
                        </div>
                      </Descriptions.Item>
                    )}
                    {Object?.keys(record?.oldData)?.length && (
                      <Descriptions.Item span={2} label="Old Data:">
                        <div>
                          {Object?.keys(record?.oldData)?.map((item, index) => (
                            <div className="d-flex" key={`record-${index + 1}`}>
                              {item} :
                              <LogDescription
                                data={record?.oldData?.[item]}
                                item={item}
                              />
                            </div>
                          ))}
                        </div>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </div>
              ),
              expandRowByClick: true,
            }}
          />
        </Card>
      </div>
    </>
  );
}

export default Logs;
